import { computed, ref, unref } from 'vue'
import { createGlobalState } from '@vueuse/core'
import useBoard from '@/hooks/use-board.js'
import { logger } from '@/utils/logger'

const useGlobalStates = createGlobalState(() => {
  const isLoading = ref({})
  const isRefreshing = ref({})
  const hierarchies = ref({})
  const hierarchyData = ref({})
  const hierarchyPerPerson = ref({})
  const activePeopleIdsCache = ref({})
  const hierarchyVersions = ref({})
  const maxHierarchyDepth = ref({})

  return {
    isLoading,
    isRefreshing,
    hierarchies,
    hierarchyData,
    hierarchyPerPerson,
    activePeopleIdsCache,
    hierarchyVersions,
    maxHierarchyDepth
  }
})

const useGlobalOptions = createGlobalState(() => {
  const options = ref({})

  return {
    options
  }
})

const activePeopleIdsLock = {}

/**
 * Hierarchy data store for the board. Using this hook doesn't trigger a fetch.
 * @param {string} viewKey - the key to map the data to. Could be a simple boardId, but for better specificity, it should include the view name.
 */
export function useHierarchyState(boardId, view = null) {
  const { boardId: mainBoardId } = useBoard()

  if (!unref(boardId)) return {}

  const viewKey = computed(() => {
    if (!unref(view)) {
      return unref(boardId)
    }

    return `${unref(boardId)}-${unref(view)}`
  })

  const {
    isLoading,
    isRefreshing,
    hierarchies,
    hierarchyData,
    hierarchyPerPerson,
    activePeopleIdsCache,
    hierarchyVersions,
    maxHierarchyDepth
  } = useGlobalStates()

  const setHierarchy = (hierarchy) => {
    hierarchies.value[unref(viewKey)] = Object.freeze(hierarchy)

    const mainHierarchy = hierarchyPerPerson.value[mainBoardId.value] ?? {}
    const hierarchyById = new Map([['_root', hierarchy]])

    let maxDepth = 0
    const processNode = (node, parentDepth = 0, parentChain = []) => {
      try {
        const nodeMainHierarchy = mainHierarchy[node.id]
        let depth
        if (node.parent.id === '_root' && node.data?.isRoot) {
          depth = 1
        } else if (
          node.parent.id === 'p_nomanagernode' ||
          node.parent.id === 'p_filtered_nodes' ||
          node.parent.id === 'p_employee_data'
        ) {
          depth = nodeMainHierarchy?.depth || 1
        } else {
          depth = parentDepth || nodeMainHierarchy?.depth || 1
        }

        node.depth = depth
        node.managementChain = parentChain
        maxDepth = Math.max(maxDepth, depth)
        hierarchyById.set(node.id, node)

        const childChain = [...parentChain, node.id]
        node.children?.forEach((child) => processNode(child, depth + 1, childChain))
      } catch (error) {
        logger.error('processNode error', error.message)
      }
    }

    hierarchy.children?.forEach((rootNode) => processNode(rootNode))
    hierarchyPerPerson.value[unref(viewKey)] = Object.freeze(Object.fromEntries(hierarchyById))
    maxHierarchyDepth.value[unref(viewKey)] = maxDepth
  }

  const setHierarchyData = (data) => {
    hierarchyData.value[unref(viewKey)] = data

    if (!activePeopleIdsLock[unref(viewKey)]) {
      activePeopleIdsLock[unref(viewKey)] = true
      const previousPeopleIds = activePeopleIdsCache.value[unref(viewKey)]
      const previousVersion = hierarchyVersions.value[unref(viewKey)]
      const { personIds, version } = computeActivePeopleIds(previousPeopleIds, previousVersion)
      activePeopleIdsCache.value[unref(viewKey)] = personIds
      hierarchyVersions.value[unref(viewKey)] = version
      activePeopleIdsLock[unref(viewKey)] = false
    }
  }

  const setIsLoading = (value) => {
    isLoading.value[unref(viewKey)] = value
  }

  const setIsRefreshing = (value) => {
    isRefreshing.value[unref(viewKey)] = value
  }

  const hierarchy = computed(() => hierarchies.value[unref(viewKey)] || null)
  const hierarchyPerson = computed(() => hierarchyPerPerson.value[unref(viewKey)] || {})
  const hierarchyRawData = computed(() => hierarchyData.value[unref(viewKey)] || [])
  const getIsLoading = computed(() => isLoading.value[unref(viewKey)] || false)
  const getIsRefreshing = computed(() => isRefreshing.value[unref(viewKey)] || false)
  const hierarchyMaxDepth = computed(() => maxHierarchyDepth.value[unref(viewKey)] || 0)

  const computeActivePeopleIds = (previousPeopleIds = [], previousVersion = 0) => {
    const data = hierarchyRawData.value
    if (!data) return { personIds: [], version: previousVersion }

    const excludedIds = new Set(['_root', 'p_nomanagernode', 'p_filtered_nodes', 'p_employee_data'])
    const result = new Array(data.length)
    let index = 0
    let version = previousVersion

    for (let i = 0; i < data.length; i++) {
      const person = data[i]
      if (!excludedIds.has(person.id) && !person.collapsed) {
        if (previousPeopleIds[index] !== person.id) {
          version++
        }
        result[index++] = person.id
      }
    }

    result.length = index

    if (previousPeopleIds.length !== index) {
      version++
    }

    return { personIds: result, version }
  }

  const activePeopleIds = computed(() => {
    return activePeopleIdsCache.value?.[unref(viewKey)] || []
  })

  const hierarchyVersion = computed(() => {
    return hierarchyVersions.value?.[unref(viewKey)] || null
  })

  return {
    hierarchy,
    hierarchyRawData,
    setHierarchy,
    setHierarchyData,
    hierarchyPerson,
    hierarchyMaxDepth,
    activePeopleIds,
    hierarchyVersion,
    setIsLoading,
    setIsRefreshing,
    getIsLoading,
    getIsRefreshing
  }
}

export function useHierarchyOptions(boardId, view = null) {
  const viewKey = computed(() => {
    if (!unref(view)) {
      return unref(boardId)
    }

    return `${unref(boardId)}-${unref(view)}`
  })

  const { options } = useGlobalOptions()

  const setOptions = (newOptions) => {
    options.value[unref(viewKey)] = newOptions
  }

  return {
    options,
    setOptions
  }
}
